import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useFetch, Panel, Tag, PageHeader, dirTone, pnlClass, Empty } from "@/components/Common";
import { api, errMsg, fmtMoney, fmtPrice, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SYMS = ["EURUSD", "GBPUSD", "USDJPY", "AUDUSD", "USDCHF", "USDCAD", "XAUUSD", "AAPL", "MSFT", "NVDA", "TSLA", "AMZN", "SPY"];

function NewTrade({ onDone }) {
  const { can } = useAuth();
  const { data: accounts } = useFetch(can("manage_brokers") ? "/brokers/accounts" : "/markets/symbols");
  const [f, setF] = useState({ symbol: "EURUSD", direction: "BUY", stop_loss: "", take_profit: "", destination: "paper", mt5_account_id: "" });
  const [busy, setBusy] = useState(false);
  const fill = async () => {
    try {
      const { data } = await api.get(`/markets/candles?symbol=${f.symbol}&interval=15min`);
      const s = data.snapshot, b = f.direction === "BUY" ? 1 : -1;
      setF({ ...f, stop_loss: (s.close - b * 1.5 * s.atr).toFixed(5), take_profit: (s.close + b * 3 * s.atr).toFixed(5) });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/trades", { ...f, stop_loss: Number(f.stop_loss), take_profit: Number(f.take_profit), mt5_account_id: f.mt5_account_id || null });
      toast.success(data.order_status === 'queued' ? 'Demo order queued · awaiting broker confirmation' : "Order placed"); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const mt5 = Array.isArray(accounts) && accounts[0]?.metaapi_account_id ? accounts : [];
  return (
    <Panel title="Manual order" testid="manual-order-panel">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-7">
        <Select value={f.symbol} onValueChange={(v) => setF({ ...f, symbol: v })}><SelectTrigger data-testid="order-symbol-select"><SelectValue /></SelectTrigger><SelectContent>{SYMS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })}><SelectTrigger data-testid="order-direction-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="BUY">BUY</SelectItem><SelectItem value="SELL">SELL</SelectItem></SelectContent></Select>
        <Input placeholder="Stop loss" value={f.stop_loss} onChange={(e) => setF({ ...f, stop_loss: e.target.value })} data-testid="order-sl-input" className="font-mono" />
        <Input placeholder="Take profit" value={f.take_profit} onChange={(e) => setF({ ...f, take_profit: e.target.value })} data-testid="order-tp-input" className="font-mono" />
        <Select value={f.destination} onValueChange={(v) => setF({ ...f, destination: v })}>
          <SelectTrigger data-testid="order-destination-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="paper">Internal Paper</SelectItem><SelectItem value="mt5_demo">MT5 Demo</SelectItem><SelectItem value="mt5_real">MT5 Real (locked)</SelectItem></SelectContent>
        </Select>
        {f.destination !== "paper" ? (
          <Select value={f.mt5_account_id} onValueChange={(v) => setF({ ...f, mt5_account_id: v })}><SelectTrigger data-testid="order-account-select"><SelectValue placeholder="MT5 account" /></SelectTrigger><SelectContent>{mt5.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>)}</SelectContent></Select>
        ) : <Button variant="outline" onClick={fill} data-testid="order-atr-fill-button">ATR levels</Button>}
        <Button onClick={submit} disabled={busy} data-testid="paper-trade-execute-button">Place order</Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Every order passes atomic risk limits and the fail-closed safety gate (fresh price, calendar freshness, news window, spread). Paper fills at the genuine quote ± modeled half-spread plus commission.</p>
    </Panel>
  );
}

function Modify({ trade, onClose }) {
  const [sl, setSl] = useState(trade?.stop_loss ?? "");
  const [tp, setTp] = useState(trade?.take_profit ?? "");
  const save = async () => {
    try { const { data } = await api.patch(`/trades/${trade.id}`, { stop_loss: Number(sl), take_profit: Number(tp) }); toast.success(data.management_pending ? 'Stop update queued · awaiting broker confirmation' : "Position modified"); onClose(true); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!trade} onOpenChange={() => onClose(false)}>
      <DialogContent data-testid="modify-trade-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Modify {trade?.symbol}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Input value={sl} onChange={(e) => setSl(e.target.value)} className="font-mono" data-testid="modify-sl-input" />
          <Input value={tp} onChange={(e) => setTp(e.target.value)} className="font-mono" data-testid="modify-tp-input" />
        </div>
        <Button onClick={save} data-testid="modify-save-button">Save</Button>
      </DialogContent>
    </Dialog>
  );
}

function TradeTable({ trades, reload }) {
  const [mod, setMod] = useState(null);
  const close = async (t) => {
    try { const { data } = await api.post(`/trades/${t.id}/close`); toast.success(data.management_pending ? 'Close queued · awaiting broker confirmation' : `Closed ${t.symbol}: ${fmtMoney(data.pnl)}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!trades?.length) return <Empty testid="trades-empty">No trades yet</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="trades-table">
        <thead><tr className="label text-left"><th className="py-2">Opened</th><th>Symbol</th><th>Side</th><th>Dest</th><th>Entry</th><th>SL / TP</th><th>Size</th><th>Exit</th><th>P/L</th><th>Status</th><th /></tr></thead>
        <tbody>{trades.map((t) => (
          <tr key={t.id} className="border-t border-border/50" data-testid={`trade-row-${t.id}`}>
            <td className="py-2 font-mono text-xs">{fmtTime(t.opened_at)}</td><td className="font-mono">{t.symbol}</td><td><Tag tone={dirTone(t.direction)}>{t.direction}</Tag></td>
            <td><Tag>{t.destination}</Tag></td><td className="num">{fmtPrice(t.entry, t.symbol)}</td>
            <td className="num text-xs">{fmtPrice(t.stop_loss, t.symbol)} / {fmtPrice(t.take_profit, t.symbol)}{t.trail_active ? <Tag tone="blue">TRAIL</Tag> : t.be_moved && <Tag tone="blue">BE</Tag>}</td>
            <td className="num text-xs">{t.lots == null ? '—' : `${t.lots} lot`}</td><td className="num">{fmtPrice(t.exit_price, t.symbol)}</td>
            <td className={`num ${pnlClass(t.pnl)}`} data-testid={`trade-pnl-${t.id}`}>{t.pnl != null ? (t.account_currency === 'GBP' ? `${Number(t.pnl).toFixed(2)} GBP` : fmtMoney(t.pnl)) : "—"}</td>
            <td>{t.status === "open" ? <><Tag tone="blue">open</Tag>{t.management && <div className="mt-1 text-[10px] text-muted-foreground">{t.management.reason} · {t.management.current_r}R · peak {t.management.peak_r}R</div>}</> : <><Tag>{t.status === 'pending' ? 'awaiting broker' : t.close_reason || t.status}</Tag>{t.pnl_source?.startsWith("estimated") && <div className="text-[10px] text-muted-foreground">Estimated P/L · broker history pending</div>}</>}</td>
            <td className="space-x-1 whitespace-nowrap text-right">{t.status === "open" && <>
              <Button size="sm" variant="outline" onClick={() => setMod(t)} data-testid={`trade-modify-${t.id}`}>Modify</Button>
              <Button size="sm" variant="destructive" onClick={() => close(t)} data-testid={`trade-close-${t.id}`}>Close</Button></>}
            </td>
          </tr>))}
        </tbody>
      </table>
      <Modify key={mod?.id} trade={mod} onClose={(ok) => { setMod(null); if (ok) reload(); }} />
    </div>
  );
}

function Journal() {
  const { data } = useFetch("/journal?limit=200", [], 30000);
  if (!data?.length) return <Empty testid="journal-empty">The AI decision journal records every candidate signal, AI verdict and risk outcome.</Empty>;
  return (
    <div className="space-y-2 scanlines" data-testid="journal-list">
      {data.map((j) => (
        <div key={j.id} className="rounded border bg-card/70 p-4" data-testid={`journal-entry-${j.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">{fmtTime(j.created_at)}</span><span className="font-mono font-semibold">{j.symbol}</span>
            <Tag>{j.desk}</Tag><Tag tone={dirTone(j.direction)}>{j.direction || "no signal"}</Tag><Tag>{j.strategy || "no setup"}</Tag>{j.ai && <Tag tone={dirTone(j.ai.decision)}>AI {j.ai.decision}</Tag>}
            <Tag tone={j.action === "executed" ? "up" : j.action === "rejected" ? "warn" : "muted"}>{j.stage}: {j.action}</Tag><Tag>{j.origin}</Tag>
          </div>
          {j.ai?.reasoning && <p className="mt-2 text-sm text-muted-foreground">{j.ai.reasoning}</p>}
          {j.master?.reason && <p className="mt-1 text-xs text-muted-foreground">Master: {j.master.reason}</p>}
          {j.reasons?.length > 0 && <p className="mt-1 font-mono text-xs text-[hsl(var(--warn))]">{j.reasons.join(" · ")}</p>}
        </div>
      ))}
    </div>
  );
}

export default function Trades() {
  const [params] = useSearchParams();
  const { can } = useAuth();
  const { data, reload } = useFetch("/trades", [], 20000);
  const canTrade = can("paper_trade") || can("demo_trade");
  return (
    <div className="space-y-4">
      <PageHeader title="Trades & Journal" sub="Paper, MT5 demo and MT5 real executions with full AI audit trail" />
      {canTrade && <NewTrade onDone={reload} />}
      <Tabs defaultValue={params.get("tab") || "open"}>
        <TabsList><TabsTrigger value="open" data-testid="tab-open">Open</TabsTrigger><TabsTrigger value="closed" data-testid="tab-closed">History</TabsTrigger><TabsTrigger value="journal" data-testid="tab-journal">AI Journal</TabsTrigger></TabsList>
        <TabsContent value="open"><Panel><TradeTable trades={data?.filter((t) => t.status === "open")} reload={reload} /></Panel></TabsContent>
        <TabsContent value="closed"><Panel><TradeTable trades={data?.filter((t) => t.status === "closed")} reload={reload} /></Panel></TabsContent>
        <TabsContent value="journal"><Journal /></TabsContent>
      </Tabs>
    </div>
  );
}
